import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Mousewheel } from 'swiper/core';

import { GiftCardSpace } from 'gift-cards/components/pdp/GiftCardSpace';
import { ArrowButton } from 'shared/components/Carousel';
import { useGiftCardsState, useGiftCardsDispatch } from 'gift-cards/hooks/useGiftCards';
import { GiftCardsActions } from 'gift-cards/state/giftCardsReducer';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useKeyDown } from 'shared/hooks/useKeyDown';
import { useWindowSize } from 'shared/hooks/useWindowSize';

SwiperCore.use([Navigation, Mousewheel]);

interface IGiftCardsCarouselDesktopProps {
  selectedIndex: number;
  setSelectedIndex: (val: number) => void;
  initialSlide?: number;
}

export const GiftCardsCarouselDesktop = ({
  selectedIndex,
  setSelectedIndex,
  initialSlide = 0,
}: IGiftCardsCarouselDesktopProps) => {
  const [swiper, setSwiper] = useState<SwiperCore>(null);
  const { spaces, spaceInMobileEditMode } = useGiftCardsState();
  const dispatch = useGiftCardsDispatch();
  const giftCards = Object.values(spaces);
  const isMounted = useIsMounted();
  const { windowSize } = useWindowSize();

  const maxHeight = 0.57 * windowSize.height < 750 ? 0.57 * windowSize.height : 750;

  const prev = () => {
    swiper?.slidePrev();
  };

  const next = () => {
    swiper?.slideNext();
  };

  useKeyDown('ArrowRight', () => {
    next();
  });

  useKeyDown('ArrowLeft', () => {
    prev();
  });

  const handleSlideChange = (swipercore) => {
    const { activeIndex: idx } = swipercore;
    setSelectedIndex(idx);
  };

  useEffect(() => {
    if (spaceInMobileEditMode && isMounted()) {
      dispatch({
        type: GiftCardsActions.setSpaceInMobileEditMode,
        payload: { spaceId: null },
      });
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isMounted() && swiper) {
        try {
          swiper?.update();
          swiper?.updateSize();
        } catch (error) {
          bugsnag(error, { context: 'Error resizing swiper of spaces' });
        }
      }
    };

    document.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      document.removeEventListener('resize', handleResize);
    };
  });

  return (
    <GiftCardsCarouselDesktop.Wrapper>
      <GiftCardsCarouselDesktop.Carousel>
        {selectedIndex !== 0 && (
          <GiftCardsCarouselDesktop.ArrowButton onArrowClick={prev} direction="left" show />
        )}
        <GiftCardsCarouselDesktop.Swiper
          allowTouchMove={false}
          centeredSlides
          initialSlide={initialSlide}
          keyboard={{
            enabled: true,
            pageUpDown: false,
          }}
          navigation={{
            prevEl: '.prev',
            nextEl: '.next',
          }}
          onClick={(_swiper) => _swiper.slideTo(_swiper.clickedIndex)}
          onSlideChange={handleSlideChange}
          onSwiper={setSwiper}
          slidesPerView="auto"
          slideToClickedSlide
          spaceBetween={48}
          watchSlidesProgress
          observer
        >
          {giftCards?.map((space) => {
            return (
              <SwiperSlide key={`slide-${space.id}`}>
                {({ isActive }) => (
                  <GiftCardSpace
                    key={space.id}
                    isActive={isActive}
                    space={space}
                    selectedObjectToFront
                    height={maxHeight < 256 ? 256 : maxHeight}
                  />
                )}
              </SwiperSlide>
            );
          })}
        </GiftCardsCarouselDesktop.Swiper>
        {selectedIndex !== giftCards.length - 1 && (
          <GiftCardsCarouselDesktop.ArrowButton onArrowClick={next} direction="right" show />
        )}
      </GiftCardsCarouselDesktop.Carousel>
    </GiftCardsCarouselDesktop.Wrapper>
  );
};

GiftCardsCarouselDesktop.Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;
  height: 100%;
  padding-bottom: 40px;

  animation: fadeIn 1s;
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

GiftCardsCarouselDesktop.Carousel = styled.div`
  width: 100vw;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  min-height: 0px;
  min-width: 0px;
  overflow: hidden;
`;

GiftCardsCarouselDesktop.ArrowButton = styled(ArrowButton)`
  position: absolute;
  top: 45vh;
  height: 45px;
  opacity: 1;
  padding: 0 32px 0 32px;
  transition-property: width, opacity;
  transition-duration: 0.2s;
  transition-timing-function: ease-out;
  z-index: ${({ theme }) => theme.zIndex.pdpFooter};

  @media (max-height: 770px) {
    top: 30vh;
  }
`;

GiftCardsCarouselDesktop.Swiper = styled(Swiper)`
  width: calc(100vw);
  height: 100%;
  display: flex;
  align-items: flex-end;

  animation: fade 1s ease-in-out;

  @keyframes fade {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .swiper-wrapper {
    margin-top: auto;
    transition-duration: 350ms !important;
  }

  .swiper-slide {
    display: flex;
    align-items: flex-end;
    width: auto !important;
  }
`;
